// Timeouts for handling animations and class removals
const REMOVE_DIRECTION_CLASS_DELAY = 300;
const ANIMATION_RESOLVE_DELAY = 100;

// Handles adding and removing page change direction classes for animation
export function onPageChanging(listenerEvent, pageElement) {
    const direction = listenerEvent.detail.direction;
    const animationClasses = {
        next: 'move-left',
        prev: 'move-right',
    };

    pageElement.classList.add(animationClasses[direction]);

    // Wait and remove the class after animation completes
    setTimeout(() => pageElement.classList.remove(animationClasses[direction]), REMOVE_DIRECTION_CLASS_DELAY);
}

// Handles dispatching an animation event for a page change
export function dispatchAnimation(direction, skipAnimation = false) {
    return new Promise(resolve => {
        if (skipAnimation) return resolve();

        window.dispatchEvent(
            new CustomEvent('dispatch-page-animation', {
                detail: { direction },
            })
        );

        // Wait for animation to settle before resolving
        setTimeout(resolve, ANIMATION_RESOLVE_DELAY);
    });
}

function mapDistanceToOpacity(distance, maxDist = 200, minDist = 1, minOpacity = 0, maxOpacity = 1) {
    // Limit values within the range
    distance = Math.min(Math.max(distance, minDist), maxDist);

    // Invert the scale so that greater distance means lower opacity
    return maxOpacity - ((distance - minDist) / (maxDist - minDist)) * (maxOpacity - minOpacity);
}

// Detects the direction and logic for page change during a swipe interaction
export function handleSwipeEvent(hammerEvent, pageElement, swipeThresholdFallback = 200) {
    // Determine swipe direction based on horizontal movement
    const swipeDirection = hammerEvent.deltaX <= 0 ? 'left' : 'right';
    const distance = Math.abs(hammerEvent.deltaX);

    // Sets the swipe threshold based on the page width and a fallback value
    const swipeThreshold = Math.min(pageElement.clientWidth * 0.25, swipeThresholdFallback);

    if (hammerEvent.type !== 'panend' && hammerEvent.type !== 'swipeHorizontalend') {
        pageElement.style.transition = 'transform 100ms ease';
        pageElement.style.opacity = `${mapDistanceToOpacity(distance, swipeThreshold)}`;
        pageElement.style.transform = `translateX(${hammerEvent.deltaX}px)`;

        return {
            status: 'panning',
            shouldDispatchPageChange: false,
            swipeDirection,
        };
    }

    if (distance < swipeThreshold) {
        resetPanPosition(pageElement);

        return {
            status: 'thresholdNotMet',
            shouldDispatchPageChange: false,
            swipeDirection,
        };
    }

    pageElement.style.transition = '';
    pageElement.style.opacity = '';

    // Delay before resetting transform, preparing for next swipe
    setTimeout(() => (pageElement.style.transform = ''), ANIMATION_RESOLVE_DELAY);

    return {
        status: 'dispatchPageChange',
        shouldDispatchPageChange: true,
        swipeDirection,
    };
}

export function addPageAnimationListener(pageElement) {
    window.addEventListener('dispatch-page-animation', event => onPageChanging(event, pageElement));
}

export function removePageAnimationListener() {
    window.removeEventListener('dispatch-page-animation', onPageChanging);
}

export function resetPanPosition(pageElement) {
    pageElement.style.transition = 'transform 0.3s ease';
    pageElement.style.opacity = '1';
    pageElement.style.transform = 'translateX(0)';

    // Delay to allow the reset styles to apply smoothly
    setTimeout(() => {
        pageElement.style.transition = '';
        pageElement.style.opacity = '';
        pageElement.style.transform = '';
    }, ANIMATION_RESOLVE_DELAY);
}

const pageTransitionHelper = {
    onPageChanging,
    dispatchAnimation,
    addPageAnimationListener,
    removePageAnimationListener,
    handleSwipeEvent,
    resetPanPosition,
};

export default pageTransitionHelper;
